import React, { useCallback, useRef, useState } from 'react';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { Props as ModalProps } from '@wix/da-ds/pkg/modals/dsPresets/Modal/Modal';
import { useEditorContext } from '../../Context';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { useTranslation } from 'react-i18next';
import { isTouch as getIsTouch } from '../../../utils/browser';

export interface Props {
  children?: (
    currentContent,
    isFullscreen: boolean,
    toggleFsOpen: () => void,
    scrollableRef: React.RefObject<HTMLElement | null> | undefined
  ) => React.ReactElement;
  modalProps?: Partial<ModalProps>;
}

const FullscreenEditor: React.FC<Props> = ({ children, modalProps }) => {
  const { t } = useTranslation('editor');
  const editor = useEditorContext();
  const isTouch = getIsTouch();
  const [fsOpen, setFsOpen] = useState(false);
  const [currentState, setCurrentState] = useState<any>(undefined);

  const toggleFs = useCallback(() => {
    setCurrentState({
      doc: editor?.getJSON(),
      selection: editor?.state.selection.toJSON(),
    });
    setFsOpen(!fsOpen);
  }, [editor, fsOpen]);

  const scrollableRef = useRef<HTMLElement | null>(null);

  return (
    <>
      {!fsOpen && (
        <div>{children?.(currentState, fsOpen, toggleFs, undefined)}</div>
      )}
      {fsOpen && (
        <Modal
          {...modalProps}
          innerScrollRef={node => {
            scrollableRef.current = node;
          }}
          isOpen={fsOpen}
          onRequestClose={toggleFs}
          isFullscreen
          useFixedMobileHeight
          ipadMode
          headerPadding={isTouch ? 'slim' : 'default'}
          bodyScrollLockOptions={{
            allowTouchMove: (el: HTMLElement) => {
              // Slider
              // Inputs like ranges
              return (
                el.closest('.menu-scrollable') !== null ||
                el.tagName === 'INPUT' ||
                el.closest('input') !== null
              );
            },
          }}
          footerRight={[
            <SolidButton
              key="done"
              onClick={toggleFs}
              size={isTouch ? 'medium' : 'large'}
              variant="approval-green"
            >
              {t('fullscreen.button.done')}
            </SolidButton>,
          ]}
        >
          {children?.(currentState, fsOpen, toggleFs, scrollableRef)}
        </Modal>
      )}
    </>
  );
};
FullscreenEditor.displayName = 'FullscreenEditor';

export default FullscreenEditor;
